import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useTheme } from "@/shared/theme/useTheme";
import type { NavGroup, NavItem } from "./nav";

// ─────────────────────────────────────────────────────────────────────────────
// Desktop vertical sidebar (lg+) — the seller portal's sidebar design applied
// to the admin portal. RTL-first: pinned to the inline-start edge via logical
// properties. Light surface (Trioty redesign). The collapse control fully
// hides the sidebar (AppShell unmounts it and shows a toggle in the top bar to
// bring it back) — there is no icon-rail state. Presentation-only — all
// data/handlers come from AppShell. Structure:
// logo + hide → admin identity card → grouped nav → footer (theme/lang/logout).
// ─────────────────────────────────────────────────────────────────────────────

export type SidebarIdentity = {
  name: string;
  /** Localized role label shown under the name. */
  roleLabel: string | null;
  initial: string;
};

type SidebarProps = {
  dashboardHref: string;
  profileHref: string;
  groups: NavGroup[];
  /** When true, the sidebar animates its width to 0 (fully hidden). */
  hidden: boolean;
  /** Hide the whole sidebar (header collapse button). */
  onHide: () => void;
  identity: SidebarIdentity;
  /** Footer content (theme toggle, language select, logout). */
  footerSlot: ReactNode;
};

export function Sidebar({
  dashboardHref,
  profileHref,
  groups,
  hidden,
  onHide,
  identity,
  footerSlot,
}: SidebarProps) {
  const t = useTranslations();
  const { resolvedDark } = useTheme();
  const appName = t(keys.common.appName);

  return (
    <aside
      aria-hidden={hidden || undefined}
      className={[
        "hidden h-full shrink-0 overflow-hidden bg-[color:var(--sidebar-surface)] transition-[width] duration-200 ease-out lg:block",
        hidden ? "border-e-0" : "border-e border-[color:var(--sidebar-border)]",
      ].join(" ")}
      style={{ width: hidden ? 0 : 264 }}
    >
      <div className="flex h-full w-[264px] flex-col overflow-hidden text-[color:var(--sidebar-ink)]">
        {/* Logo + hide toggle */}
        <div className="flex h-16 shrink-0 items-center justify-between px-4">
          <Link
            to={dashboardHref}
            aria-label={appName}
            className="flex items-center"
          >
            <img
              src={resolvedDark ? "/logo-sidebar-dark.svg" : "/logo-sidebar.svg"}
              alt={appName}
              className="h-7 w-auto object-contain"
            />
          </Link>
          <button
            type="button"
            onClick={onHide}
            aria-label={t(keys.admin.navCollapse)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] text-[color:var(--sidebar-ink-disabled)] transition-colors hover:bg-[color:var(--sidebar-hover-bg)] hover:text-[color:var(--sidebar-ink)]"
          >
            <CollapseIcon />
          </button>
        </div>

        {/* Admin identity card → profile */}
        <div className="px-3">
          <Link
            to={profileHref}
            aria-label={t(keys.admin.profileTitle)}
            className="block rounded-[13px] border border-[color:var(--sidebar-border)] bg-[color:var(--sidebar-surface-elevated)] px-3 py-2.5 transition-colors hover:border-[color:var(--border-strong)]"
          >
            <div className="flex items-center gap-2.5">
              <div
                aria-hidden="true"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] text-[12px] font-extrabold text-white"
                style={{ background: "linear-gradient(135deg, #1f2c4d, #2f4170)" }}
              >
                {identity.initial}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-bold leading-tight text-[color:var(--sidebar-ink)]">
                  {identity.name}
                </div>
                {identity.roleLabel ? (
                  <div className="mt-0.5 truncate text-[10.5px] font-medium text-[color:var(--sidebar-ink-disabled)]">
                    {identity.roleLabel}
                  </div>
                ) : null}
              </div>
            </div>
          </Link>
        </div>

        {/* Nav (scroll region) */}
        <nav className="no-scrollbar mt-2 flex-1 overflow-y-auto px-3 pb-2">
          {groups.map((group, gi) => (
            <div
              key={group.header ?? `primary-${gi}`}
              className={gi > 0 ? "mt-4" : ""}
            >
              {group.header ? (
                <div className="flex items-center gap-2 px-3 pb-1.5 pt-1 text-[11px] font-bold uppercase tracking-[0.04em] text-[color:var(--sidebar-ink-disabled)]">
                  <span
                    aria-hidden="true"
                    className="inline-block h-[13px] w-[13px] rounded-full border-[1.5px] border-dashed border-[color:var(--border-strong)]"
                  />
                  {group.header}
                </div>
              ) : null}
              <ul className="flex flex-col gap-0.5">
                {group.items.map((item) => (
                  <li key={item.key}>
                    <NavRow item={item} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Footer — theme, language, logout */}
        <div className="shrink-0 border-t border-[color:var(--sidebar-border)] p-3">
          {footerSlot}
        </div>
      </div>
    </aside>
  );
}

// ─── Nav row ──────────────────────────────────────────────────────────────────

function NavRow({ item }: { item: NavItem }) {
  return (
    <Link
      to={item.href}
      aria-current={item.isActive ? "page" : undefined}
      className={[
        "relative flex min-h-[40px] w-full items-center gap-3 rounded-[10px] px-3 text-[13.5px] transition-colors",
        item.isActive
          ? "bg-[color:var(--sidebar-active-bg)] font-semibold text-[color:var(--sidebar-active-ink)] shadow-[inset_0_0_0_1px_var(--pillring)]"
          : "font-medium text-[color:var(--sidebar-ink-muted)] hover:bg-[color:var(--sidebar-hover-bg)] hover:text-[color:var(--sidebar-ink)]",
      ].join(" ")}
    >
      <span className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center">
        {item.icon}
      </span>
      <span className="min-w-0 flex-1 truncate text-start">{item.label}</span>
    </Link>
  );
}

function CollapseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2.5" />
      <path d="M9 3v18" />
    </svg>
  );
}
